import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getCart, getTotal, clearCart } from "../cart/cart";
import { createOrder } from "../api/orders";
import { SHIPPING, PAYMENT } from "../api/config";

function fmt(n) {
    return new Intl.NumberFormat("sk-SK", { style: "currency", currency: "EUR" }).format(n || 0);
}
function loadLS(key, fallback) {
    try {
        var t = localStorage.getItem(key);
        return t ? JSON.parse(t) : fallback;
    } catch(e) { return fallback; }
}

function ConfirmPage() {
    var navigate = useNavigate();
    var cart = useMemo(getCart, []);
    var checkout = loadLS("checkout_v1", null);   // { ship, pay }
    var customer = loadLS("customer_v1", null);   // { firstName, ... }

    var [sending, setSending] = useState(false);

    if (!cart.items.length) {
        return (
            <div className="container py-4">
                <h1 className="h4 mb-3">Zhrnutie objednávky</h1>
                <div className="alert alert-light border">Košík je prázdny.</div>
                <Link to="/ponuka" className="btn btn-brown">Späť na ponuku</Link>
            </div>
        );
    }
    if (!checkout) {
        return (
            <div className="container py-4">
                <h1 className="h4 mb-3">Zhrnutie objednávky</h1>
                <div className="alert alert-warning">Prosím vyberte dopravu a platbu.</div>
                <Link to="/objednavka" className="btn btn-brown">Prejsť na výber dopravy/platby</Link>
            </div>
        );
    }
    if (!customer) {
        return (
            <div className="container py-4">
                <h1 className="h4 mb-3">Zhrnutie objednávky</h1>
                <div className="alert alert-warning">Prosím vyplňte údaje zákazníka.</div>
                <Link to="/udaje" className="btn btn-brown">Prejsť na údaje</Link>
            </div>
        );
    }

    var shipCfg = SHIPPING[checkout.ship] || SHIPPING.kurier;
    var payCfg  = PAYMENT[checkout.pay]   || PAYMENT.kartou;

    var subtotal = getTotal();
    var shipping = shipCfg.price;
    var payment  = payCfg.price;
    var total    = subtotal + shipping + payment;

    function buildPayload() {
        return {
            createdAt: new Date().toISOString(),
            items: cart.items.map(function(it){
                return {
                    productId: it.id,
                    name: it.name,
                    unitPrice: it.price,
                    quantity: it.qty,
                    slug: it.slug
                };
            }),
            pricing: {
                subtotal: subtotal,
                shipping: {
                    method: checkout.ship,
                    label: shipCfg.label,
                    price: shipping
                },
                payment:  {
                    method: checkout.pay,
                    label: payCfg.label,
                    fee: payment
                },
                total: total
            },
            customer: {
                firstName: customer.firstName,
                lastName:  customer.lastName,
                email:     customer.email,
                phone:     customer.phone,
                address:   customer.address,
                zip:       customer.psc,
                city:      customer.city,
                country:   customer.country
            }
        };
    }

    async function handleConfirm() {
        try {
            setSending(true);
            var payload = buildPayload();          // už máš hotové
            var created = await createOrder(payload); // { id, status, ... }
            clearCart();
            if (created && created.id != null) {
                navigate("/dakujeme?orderId=" + created.id);
            } else {
                navigate("/dakujeme");
            }
        } catch (e) {
            alert("Odoslanie sa nepodarilo. Skús neskôr.");
        } finally {
            setSending(false);
        }
    }

    return (
        <div className="container py-4">
            <h1 className="h4 mb-4">Zhrnutie objednávky</h1>

            <div className="row g-4">
                {/* ĽAVO – položky + údaje */}
                <div className="col-lg-8">
                    <div className="card mb-4">
                        <div className="card-body">
                            <h2 className="h5 mb-3">Položky v košíku</h2>
                            <ul className="list-group">
                                {cart.items.map(function(it){
                                    return (
                                        <li key={it.id} className="list-group-item">
                                            <div className="d-flex align-items-center gap-3">
                                                <img
                                                    src={it.imageUrl || "/images/placeholder.jpg"}
                                                    onError={function(e){ e.currentTarget.src="/images/placeholder.jpg"; }}
                                                    alt={it.name} width="64" height="64" style={{ objectFit:"cover" }}
                                                />
                                                <div className="flex-grow-1">
                                                    <div className="fw-semibold">{it.name}</div>
                                                    <div className="text-muted small">Množstvo: {it.qty}</div>
                                                </div>
                                                <div className="fw-semibold">{fmt((it.price || 0) * (it.qty || 0))}</div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-body">
                            <h2 className="h5 mb-3">Údaje zákazníka</h2>
                            <div className="row">
                                <div className="col-md-6 mb-2"><strong>Meno:</strong> {customer.firstName} {customer.lastName}</div>
                                <div className="col-md-6 mb-2"><strong>Tel. číslo:</strong> {customer.phone}</div>
                                <div className="col-12 mb-2"><strong>E-mail:</strong> {customer.email}</div>
                                <div className="col-12 mb-2"><strong>Adresa:</strong> {customer.address}, {customer.psc} {customer.city}, {customer.country?.toUpperCase()}</div>
                                {/*<div className="col-md-6 mb-2"><strong>Mesto:</strong> {customer.city}</div>*/}
                                {/*<div className="col-md-6 mb-2"><strong>Štát:</strong> {customer.country?.toUpperCase()}</div>*/}
                            </div>
                            <Link to="/udaje" className="btn btn-link p-0 mt-2">Upraviť údaje</Link>
                        </div>
                    </div>
                </div>

                {/* PRAVO – súhrn ceny + doprava/platba */}
                <div className="col-lg-4">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between mb-2">
                                <span>Medzisúčet</span><span className="fw-semibold">{fmt(subtotal)}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span>Doprava ({shipCfg.label})</span><span>{fmt(shipping)}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span>Platba ({payCfg.label})</span><span>{fmt(payment)}</span>
                            </div>
                            <hr />
                            <div className="d-flex justify-content-between">
                                <span className="fw-bold">Spolu</span>
                                <span className="fw-bold fs-5">{fmt(total)}</span>
                            </div>

                            <div className="d-grid mt-3">
                                <button className="btn btn-brown" onClick={handleConfirm} disabled={sending}>
                                    {sending ? "Odosielam…" : "Odoslať objednávku"}
                                </button>
                            </div>

                            <div className="d-flex justify-content-between mt-3">
                                <Link to="/objednavka" className="btn btn-outline-secondary btn-sm rounded-pill">Zmeniť dopravu/platbu</Link>
                                <Link to="/kosik" className="btn btn-outline-secondary btn-sm rounded-pill">Späť do košíka</Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ConfirmPage;
